import {
  defaultLocationFormValues,
  type LocationFormValues,
} from '@/definitions/location'
import type { TaxNumberType } from '@/definitions/organization'

/**
 * Every field is a string because HTML inputs are strings. `tax_rate` is held as
 * the percentage the user types and converted to the decimal fraction the API
 * stores on submit, and an empty `federal_tax_type` or `state` means "not set" —
 * all three are omitted from their request rather than sent blank.
 *
 * The location half is `LocationFormValues` rather than its own copy of those
 * fields, so this form satisfies the same contract as every other surface that
 * collects a location.
 *
 * The form spans three records, so a field is prefixed when its bare name is
 * already taken by another of them: the location's own display name is
 * `location_name` because `display_name` is the organization's, and the admin's
 * fields carry `admin_` because a practice's NPI and the org's are different
 * things.
 *
 * The admin's primary location is not here. It is not collected — it is the
 * location this same flow just created, so it is passed to the request builder
 * from the id the flow already holds rather than being asked for.
 */
export interface CreateOrgFormValues extends LocationFormValues {
  org_name: string
  display_name: string
  federal_tax_id: string
  federal_tax_type: TaxNumberType | ''
  tax_rate: string
  admin_name_given: string
  admin_name_family: string
  admin_username: string
  admin_email: string
  admin_temporary_password: string
  admin_npi: string
  admin_license_number: string
}

export const defaultFormValues: CreateOrgFormValues = {
  org_name: '',
  display_name: '',
  federal_tax_id: '',
  federal_tax_type: '',
  tax_rate: '',
  admin_name_given: '',
  admin_name_family: '',
  admin_username: '',
  admin_email: '',
  admin_temporary_password: '',
  admin_npi: '',
  admin_license_number: '',
  ...defaultLocationFormValues,
}

/** Enforced by the backend request schema; mirrored here so the UI can warn first. */
export const ORG_NAME_MIN_LENGTH = 10
export const DISPLAY_NAME_MAX_LENGTH = 35
export const TEMPORARY_PASSWORD_MIN_LENGTH = 10

export const TAX_TYPE_OPTIONS: { value: TaxNumberType; label: string }[] = [
  { value: 'ein', label: 'EIN' },
  { value: 'ssn', label: 'SSN' },
]
